import type { Court, Id, Match, Phase, QueueEntry, Tournament } from '../types.js';
import { effectiveRules } from '../rules/presets.js';
import { slotTeamIds } from '../slots.js';

/**
 * Warteschlange (docs/04 §4 Modus B). Kein Zeitplan — wird ein Feld frei, zieht
 * das Tool das erste Spiel, dessen beide Teams frei sind und deren Mindestpause
 * abgelaufen ist. Reihenfolge: Bewerb → Phase → Priorität → Runde →
 * wenigste bisher gespielte Spiele (kein Team spielt ständig) → Nummer.
 */

interface TeamState {
  busy: boolean;
  lastEndedAt?: number;
  withdrawn: boolean;
  /** beendete + laufende Spiele — für die faire Reihenfolge */
  played: number;
}

function teamStates(t: Tournament): Map<Id, TeamState> {
  const st = new Map<Id, TeamState>();
  for (const c of t.competitions) {
    for (const team of c.teams) st.set(team.id, { busy: false, withdrawn: team.status === 'withdrawn' || team.status === 'disqualified', played: 0 });
    for (const p of c.phases) {
      for (const m of p.matches) {
        for (const id of [...slotTeamIds(m.a), ...slotTeamIds(m.b)]) {
          const ts = st.get(id);
          if (!ts) continue;
          if (m.status === 'running') ts.busy = true;
          if (m.status === 'running' || m.status === 'finished' || m.status === 'walkover') ts.played++;
          if (m.endedAt !== undefined && (ts.lastEndedAt === undefined || m.endedAt > ts.lastEndedAt)) ts.lastEndedAt = m.endedAt;
        }
      }
    }
  }
  return st;
}

/** Zustand einer Seite — bei wechselnden Partnern beide Spieler zusammen: frei erst, wenn beide frei sind */
function sideState(states: Map<Id, TeamState>, slot: Match['a']): TeamState | undefined {
  const list = slotTeamIds(slot).map((id) => states.get(id));
  if (list.length === 0 || list.some((x) => !x)) return undefined;
  const all = list as TeamState[];
  const ended = all.map((x) => x.lastEndedAt).filter((x): x is number => x !== undefined);
  const out: TeamState = { busy: all.some((x) => x.busy), withdrawn: all.some((x) => x.withdrawn), played: Math.max(...all.map((x) => x.played)) };
  if (ended.length) out.lastEndedAt = Math.max(...ended);
  return out;
}

export function activePhases(t: Tournament): { phase: Phase; competitionIndex: number }[] {
  const out: { phase: Phase; competitionIndex: number }[] = [];
  t.competitions.forEach((c, ci) => {
    for (const p of c.phases) if (p.status === 'drawn' || p.status === 'running') out.push({ phase: p, competitionIndex: ci });
  });
  return out;
}

/** Pause (docs/17 §8): läuft gerade eine? */
export function isPaused(t: Tournament, now: number): boolean {
  return t.pausedUntil !== undefined && t.pausedUntil > now;
}

export function buildQueue(t: Tournament, now: number): QueueEntry[] {
  const states = teamStates(t);
  const paused = isPaused(t, now);
  const entries: (QueueEntry & { sort: number[] })[] = [];
  for (const { phase, competitionIndex } of activePhases(t)) {
    const comp = t.competitions[competitionIndex];
    if (!comp) continue;
    const rules = effectiveRules(comp.ruleSet, phase.ruleSetOverride);
    for (const m of phase.matches) {
      if (m.status !== 'ready') continue;
      const a = sideState(states, m.a);
      const b = sideState(states, m.b);
      let blockedBy: string | undefined;
      if (!a || !b) blockedBy = 'Teilnehmer unbekannt';
      else if (a.withdrawn || b.withdrawn) blockedBy = 'Team zurückgezogen';
      else if (paused) blockedBy = 'Pause';
      else if (a.busy || b.busy) blockedBy = 'Team noch auf dem Feld';
      else if (rules.minRestMinutes > 0) {
        const last = Math.max(a.lastEndedAt ?? -Infinity, b.lastEndedAt ?? -Infinity);
        const restUntil = last + rules.minRestMinutes * 60_000;
        if (Number.isFinite(last) && restUntil > now) blockedBy = `Pause bis ${Math.ceil((restUntil - now) / 60_000)} min`;
      }
      const waitingSince = Math.min(a?.lastEndedAt ?? now, b?.lastEndedAt ?? now);
      const entry: QueueEntry & { sort: number[] } = {
        match: m,
        startable: blockedBy === undefined,
        waitingMinutes: Math.max(0, Math.round((now - waitingSince) / 60_000)),
        sort: [competitionIndex, phase.order, -m.priority, m.round, Math.max(a?.played ?? 0, b?.played ?? 0), m.number],
      };
      if (blockedBy !== undefined) entry.blockedBy = blockedBy;
      entries.push(entry);
    }
  }
  entries.sort((x, y) => {
    for (let i = 0; i < x.sort.length; i++) {
      const d = (x.sort[i] as number) - (y.sort[i] as number);
      if (d !== 0) return d;
    }
    return 0;
  });
  return entries.map(({ sort: _sort, ...e }) => e);
}

export function freeCourts(t: Tournament): Court[] {
  return t.courts.filter((c) => c.active && !c.currentMatchId).sort((x, y) => y.priority - x.priority || x.name.localeCompare(y.name));
}

/**
 * Platz (Feldgruppe), auf dem ein Spiel laufen muss: KO-Runde bzw. Gruppe vor
 * Phase (docs/17, docs/notes/ko-runden.md)
 */
export function matchCourtGroup(t: Tournament, m: Match): string | undefined {
  for (const c of t.competitions) {
    const p = c.phases.find((ph) => ph.id === m.phaseId);
    if (!p) continue;
    const round = p.koRounds?.find((r) => r.matchIds.includes(m.id));
    if (round) return round.courtGroup ?? p.config.courtGroup;
    const g = m.groupId ? p.groups.find((x) => x.id === m.groupId) : undefined;
    return g?.courtGroup ?? p.config.courtGroup;
  }
  return undefined;
}

function courtAllowed(t: Tournament, m: Match, court: Court): boolean {
  const group = matchCourtGroup(t, m);
  return !group || court.group === group;
}

export interface ForecastEntry {
  matchId: Id;
  /** voraussichtlicher Beginn (ms) */
  startsAt: number;
  /** Feld, auf dem es voraussichtlich läuft */
  courtId?: Id;
  /** Position in der Warteschlange (1 = nächstes) */
  position: number;
}

/**
 * Vorschau (docs/17): simuliert die Warteschlange nach vorn — welche Spiele
 * kommen wann ungefähr dran? Greedy wie die Auto-Zuweisung: für jedes Spiel in
 * Reihenfolge das früheste Feld, an dem beide Teams frei sind; Dauer = Ø-Dauer
 * plus Wechselzeit. Grob, aber genau das, was Spieler wissen wollen („so in
 * 40 Minuten").
 */
export function forecastQueue(t: Tournament, now: number): ForecastEntry[] {
  const changeoverMs = 2 * 60_000;
  const courts = t.courts.filter((c) => c.active);
  if (courts.length === 0) return [];
  const start0 = Math.max(now, t.pausedUntil ?? 0);
  const courtFree = new Map<Id, number>(courts.map((c) => [c.id, start0]));
  const teamFree = new Map<Id, number>();
  const avgOf = (m: Match): number => {
    for (const c of t.competitions) {
      const p = c.phases.find((ph) => ph.id === m.phaseId);
      if (p) return (c.measuredMatchMinutes ?? effectiveRules(c.ruleSet, p.ruleSetOverride).plannedMatchMinutes) * 60_000;
    }
    return 20 * 60_000;
  };
  // laufende Spiele belegen Feld und Teams bis zum geschätzten Ende
  for (const c of t.competitions) {
    for (const p of c.phases) {
      for (const m of p.matches) {
        if (m.status !== 'running') continue;
        const end = Math.max(now, (m.startedAt ?? now) + avgOf(m)) + changeoverMs;
        if (m.courtId && courtFree.has(m.courtId)) courtFree.set(m.courtId, end);
        for (const id of [...slotTeamIds(m.a), ...slotTeamIds(m.b)]) teamFree.set(id, end);
      }
    }
  }
  // Warteschlange ohne Pausen-/Belegt-Sperren — die löst die Simulation selbst auf
  const queue = buildQueue(t, now).filter((e) => e.blockedBy !== 'Team zurückgezogen' && e.blockedBy !== 'Teilnehmer unbekannt');
  for (const id of teamFree.keys()) teamFree.set(id, Math.max(teamFree.get(id) ?? start0, start0));
  const out: ForecastEntry[] = [];
  queue.forEach((e, i) => {
    const m = e.match;
    const ids = [...slotTeamIds(m.a), ...slotTeamIds(m.b)];
    const teamsReady = Math.max(...ids.map((id) => teamFree.get(id) ?? start0));
    const allowed = courts.filter((c) => courtAllowed(t, m, c));
    if (allowed.length === 0) return;
    const court = allowed.reduce((best, c) => ((courtFree.get(c.id) ?? now) < (courtFree.get(best.id) ?? now) ? c : best));
    const start = Math.max(courtFree.get(court.id) ?? now, teamsReady);
    const end = start + avgOf(m) + changeoverMs;
    courtFree.set(court.id, end);
    for (const id of ids) teamFree.set(id, end);
    out.push({ matchId: m.id, startsAt: start, courtId: court.id, position: i + 1 });
  });
  return out.sort((a, b) => a.startsAt - b.startsAt);
}

/**
 * Bestes Spiel für ein Feld: Spiele, die **genau diesen Platz brauchen**, gehen
 * vor — sonst belegen platzungebundene Spiele die Felder von Platz 2, und die
 * dort gebundenen Gruppen warten (docs/17).
 */
function pickForCourt(t: Tournament, court: Court, queue: readonly QueueEntry[], ok: (e: QueueEntry) => boolean): QueueEntry | undefined {
  let fallback: QueueEntry | undefined;
  for (const e of queue) {
    if (!ok(e) || !courtAllowed(t, e.match, court)) continue;
    if (court.group && matchCourtGroup(t, e.match) === court.group) return e;
    fallback ??= e;
  }
  return fallback;
}

/** Nächstes startbares Spiel für ein bestimmtes Feld */
export function nextMatchForCourt(t: Tournament, courtId: Id, now: number): Match | undefined {
  const court = t.courts.find((c) => c.id === courtId);
  if (!court || !court.active || court.currentMatchId) return undefined;
  return pickForCourt(t, court, buildQueue(t, now), (e) => e.startable)?.match;
}

/**
 * Vorschlag für Auto-Zuweisung: für jedes freie Feld das nächste passende Spiel.
 * Reine Funktion — die Zuweisung selbst läuft über `match.start`-Aktionen.
 */
export function suggestAssignments(t: Tournament, now: number): { matchId: Id; courtId: Id }[] {
  const out: { matchId: Id; courtId: Id }[] = [];
  const taken = new Set<Id>();
  const busyTeams = new Set<Id>();
  const queue = buildQueue(t, now).filter((e) => e.startable);
  for (const court of freeCourts(t)) {
    const e = pickForCourt(t, court, queue, (q) => {
      if (taken.has(q.match.id)) return false;
      const ids = [...slotTeamIds(q.match.a), ...slotTeamIds(q.match.b)];
      return !ids.some((id) => busyTeams.has(id));
    });
    if (!e) continue;
    taken.add(e.match.id);
    for (const id of [...slotTeamIds(e.match.a), ...slotTeamIds(e.match.b)]) busyTeams.add(id);
    out.push({ matchId: e.match.id, courtId: court.id });
  }
  return out;
}
